import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { logout, isAuthenticated } from '../services/authService';
import './Header.css';
import { useTranslation } from 'react-i18next';
import AccountCircleIcon from '@mui/icons-material/AccountCircle'; // Import the icon
import IconButton from '@mui/material/IconButton'; // Import IconButton for better icon button styling
import Chip from '@mui/material/Chip';
import BusinessIcon from '@mui/icons-material/Business';
import MenuIcon from '@mui/icons-material/Menu'; // For mobile menu button
import Menu from '@mui/material/Menu'; // For dropdown menu
import MenuItem from '@mui/material/MenuItem'; // For dropdown menu items
import Avatar from '@mui/material/Avatar'; // For profile icon in menu
import ListItemIcon from '@mui/material/ListItemIcon'; // For icons in menu items
import ListItemText from '@mui/material/ListItemText'; // For text in menu items
import ExitToAppIcon from '@mui/icons-material/ExitToApp'; // For logout icon
import Divider from '@mui/material/Divider'; // For separator in menu
import useMediaQuery from '@mui/material/useMediaQuery'; // For responsive design
import { useTheme } from '@mui/material/styles'; // To access theme breakpoints
import { useUser } from '../contexts/UserContext'; // Import useUser
import { useTenant } from '../contexts/TenantContext'; // Import useTenant
import { useEvents } from '../contexts/EventContext'; // Import useEvents

const Header = ({ title = "BananApp" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authenticated = isAuthenticated();
  const { userData, userProfile, clearUserData } = useUser(); // Get userData and clearUserData from context
  const { tenantData, switchTenant, getAvailableTenants, selectedTenantId } = useTenant(); // Get tenant context
  const { changeTenantIDCleanup } = useEvents(); // Get changeTenantIDCleanup from context
  const { tenant_id } = useParams(); // Extract tenant_id from URL

  // Add state and hooks for mobile menu
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for tenant switcher menu
  const [tenantMenuAnchorEl, setTenantMenuAnchorEl] = useState(null);
  const openTenantMenu = Boolean(tenantMenuAnchorEl);

  const [availableTenants, setAvailableTenants] = useState([]); // Manage availableTenants as state
  
  useEffect(() => {
    const fetchTenants = async () => {
      if (getAvailableTenants) {
        try {
          const tenants = await getAvailableTenants(); // Await the Promise
          if (tenants && tenants.length > 0) {
            setAvailableTenants(tenants);
            console.log('Header: Available tenants updated:', tenants);
          } else {
            console.warn('Header: No available tenants returned by getAvailableTenants.');
          }
        } catch (error) {
          console.error('Header: Error while fetching available tenants:', error);
        }
      } else {
        console.error('Header: getAvailableTenants function is not defined.');
      }
    };

    fetchTenants();
  }, [getAvailableTenants, tenantData, selectedTenantId]); // Update when tenant context changes

  // Determine logo URL
  const logoUrl = tenantData?.logo_url || process.env.PUBLIC_URL + '/logo_lumiere.png';

  // Handlers for the mobile menu
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handlers for tenant switcher menu
  const handleTenantMenuOpen = (event) => {
    setTenantMenuAnchorEl(event.currentTarget);
  };

  const handleTenantMenuClose = () => {
    setTenantMenuAnchorEl(null);
  };

  const handleTenantSwitch = (tenantId) => {
    if (switchTenant) {
      changeTenantIDCleanup(); // Clean up events and related data
      switchTenant(tenantId);
    }
    handleTenantMenuClose();
    // Optionally, navigate to dashboard or refresh data
    // navigate('/dashboard'); 
    // window.location.reload(); // Or use a more specific data refresh mechanism
  };
  
  // Navigation handlers that also close the menu
  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleLogout = () => {
    console.log('Logging out user and clearing data from tenant ', tenant_id);
    handleMenuClose();
    logout(); // This is the original logout from authService
    clearUserData(); // Call clearUserData from context, which also clears localStorage
    if (localStorage.getItem("LOGIN_PAGE")) {
      console.log("Redirecting to saved login page");
      const loginPage = localStorage.getItem("LOGIN_PAGE");
      // localStorage.removeItem("LOGIN_PAGE");
      console.log("Redirecting to saved login page:", `${loginPage}login`);
      navigate(`${loginPage}login?toto=1`);
      return;
    } else {
      console.log("Redirecting to default login page");
      navigate(`${tenant_id ? '/app/' + tenant_id : ''}/login`);
    }

    
  };

  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      id={menuId}
      open={open}
      onClose={handleMenuClose}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
      }}
    >
      <MenuItem onClick={handleProfileClick}>
        <ListItemIcon>
          <AccountCircleIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('header.profile')} />
      </MenuItem>
      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <ExitToAppIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('header.logout')} />
      </MenuItem>
    </Menu>
  );

  return (
    <header 
      className="app-header"
      style={{ 
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary // Or another color for good contrast
      }}
    >
      <div className="header-left">
        <img
          src={logoUrl} // Use the determined logoUrl
          alt={t('header.logoAlt')}
          className="app-logo-image"
          onClick={() => navigate(authenticated ? '/dashboard' : '/')}
        />
        <div className="app-logo" onClick={() => navigate(authenticated ? '/dashboard' : '/')}>
          {tenantData?.metadatas?.application_title || t('header.appTitle')}
        </div>
      </div>

      <div className="header-actions">
        {authenticated ? (
          <>
            {/* Unified Menu for both Desktop and Mobile */}
            <IconButton
              onClick={handleMenuOpen} // This opens the main profile/tenant/logout menu
              color="primary"
              aria-controls={open ? 'profile-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              title={t('header.menu')}
            >
              {
                userProfile && userProfile.photo ? (
                  <Avatar alt={userProfile.full_name} src={userProfile.photo} />
                ) : (
                  <AccountCircleIcon fontSize="large" />
                )
              }
            </IconButton>
            <Menu
              id="profile-menu"
              anchorEl={anchorEl} // Main menu anchor
              open={open} // Main menu open state
              onClose={handleMenuClose} // Main menu close handler
              MenuListProps={{
                'aria-labelledby': 'profile-button',
              }}
              PaperProps={{
                elevation: 3,
                sx: { mt: 1, minWidth: 220 } // Adjusted minWidth for better content display
              }}
            >
              {/* User info with tenant if available */}
              {userData && (
                <MenuItem disabled sx={{ opacity: 1, '&:hover': { backgroundColor: 'transparent' } }}>
                  <ListItemText 
                    primary={userData.full_name || t('header.userProfile')}
                    secondary={userData.email}
                    primaryTypographyProps={{ noWrap: true, fontWeight: 'bold' }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </MenuItem>
              )}
              
              {/* Tenant info if available - now part of the main dropdown */}
              {tenantData && tenantData.name && (
                <MenuItem disabled sx={{ opacity: 1, '&:hover': { backgroundColor: 'transparent' } }}>
                  <ListItemIcon sx={{ minWidth: '36px'}}>
                    <BusinessIcon color="action" />
                  </ListItemIcon>
                  <ListItemText primary={tenantData.name} />
                </MenuItem>
              )}
              
              {(userData || tenantData) && <Divider sx={{ my: 0.5 }}/>}

              {/* Tenant Switcher for Mobile & Desktop */}
              {availableTenants && availableTenants.length > 1 && [
                <MenuItem key="openTenantMenu" onClick={handleTenantMenuOpen}>
                  <ListItemIcon sx={{ minWidth: '36px' }}>
                    <BusinessIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('header.switchTenant') || "Switch Tenant"} />
                </MenuItem>,
                <Menu
                  key="tenantMenu"
                  anchorEl={tenantMenuAnchorEl}
                  open={openTenantMenu}
                  onClose={handleTenantMenuClose}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  PaperProps={{ elevation: 3, sx: { mt: 1, minWidth: 200 } }}
                >
                  {availableTenants.map((tenant) => (
                    <MenuItem
                      key={tenant.tenant_id}
                      selected={tenant.tenant_id === (selectedTenantId || tenantData?.tenant_id)}
                      onClick={() => handleTenantSwitch(tenant.tenant_id)}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          '&:hover': { backgroundColor: theme.palette.primary.dark },
                        },
                        '&:hover': { backgroundColor: theme.palette.action.hover },
                      }}
                    >
                      {tenant.name}
                    </MenuItem>
                  ))}
                </Menu>
              ]}
              
              {/* Profile link */}
              <MenuItem onClick={handleProfileClick}>
                <ListItemIcon sx={{ minWidth: '36px'}}>
                  <AccountCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={t('header.profile')} />
              </MenuItem>
              
              {/* Logout option */}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon sx={{ minWidth: '36px'}}>
                  <ExitToAppIcon color="error" />
                </ListItemIcon>
                <ListItemText primary={t('header.logout')} />
              </MenuItem>
            </Menu>
          </>
        ) : (
          <button onClick={() => navigate('/')} className="login-button">{t('header.login')}</button>
        )}
      </div>
    </header>
  );
};

export default Header;
